import React, { useState, useRef, useCallback, useMemo, useEffect, forwardRef, useContext, memo } from "react";
import ReactPlayer from "react-player/lazy";
import styled from "styled-components";
import PlayerControls from "./PlayerControls";
import CircularProgress from "./CircularProgress";
import { formatTime } from "../shared/utils/helper";
import { theme } from "../styles/theme";

import {ProgressContext} from "src/ProgressContext";

interface VideoPlayerProps {
  vidSrc: any;
  size?: number;
  playsize?: number;
  hidePlayVideoIcon?: boolean;
  hideSeek?: boolean;
  autoPlay?: boolean;
  play?: boolean;
  playOnClick?: boolean;
  showProgress?: boolean;
  showTimer?: boolean;
  onReady: (status: boolean) => void;
  onProgress: (played: number) => void;
  onDuration: (duration: number) => void;
  onPause: (status: "PLAYED" | "PLAYING" | "TOPLAY") => void;
  onPlay: () => void;
  onVideoEnded: () => void;
}


const StyledPlayerWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden; 
  zIndex: 99999999999;

  border-radius: 50%;
  clip-path: circle(50% at center); 

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }

  iframe {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
`;

const TimerCounter = styled.div`
  width: 82px;
  height: 30px;
  position: absolute;
  background: #0000004D;
  top: 20px;
  left: 50%;
  transform: translate(-50%, -50%);
  gap: 4px;
  border-radius: 28px;
  padding: 8px 10px;
  font-family: ${theme.fonts.primary};
  font-weight: ${theme.fontWeights.bold};
  font-size: ${theme.fontSizes.sm};
  line-height: 100%;
  text-align: center;
`;


const VideoPlayer = forwardRef<ReactPlayer, VideoPlayerProps>(
  (
    {
      vidSrc,
      size = 90,
      hidePlayVideoIcon,
      hideSeek,
      autoPlay,
      playOnClick,
      play,
      showProgress = true,
      showTimer = false,
      onProgress,
      onDuration,
      onPause,
      onPlay,
      onReady,
      onVideoEnded,
      playsize,
      ...rest
    },
    ref
  ) => {
    const [playing, setPlaying] = useState<boolean>(autoPlay || false);
    const [progress, setProgress] = useState<number>(0);
    const [timer, setTimer] = useState<number>(0);
    const [duration, setDuration] = useState<number>(0);
    const [seeking, setSeeking] = useState<boolean>(false);
    const [status, setStatus] = useState<"PLAYED" | "PLAYING" | "TOPLAY">("TOPLAY");

   
    const sourceUrlRef = useRef<string>("");
    
    const videoElementRef = useRef<HTMLVideoElement>(null);
    const playerRef = useRef<ReactPlayer | null>(null);
    const previousSourceRef = useRef<any>(null);

    const [countDown, setCountDown] = useState<string>("");

    const [isVideoLoaded, setIsVideoLoaded] = useState(false);
    const [isVideoVisible, setIsVideoVisible] = useState(false);

    const { setProgressTracker } = useContext(ProgressContext);

    const previousPlayRef = useRef(play);
    const initialVidSrcRef = useRef<boolean>(false);

    
    const setPlayerRef = useCallback((node) => {
      if (!node) return;
      playerRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<ReactPlayer | null>).current = node;
      }
    }, [ref]);

    
    const handleProgress = useCallback((state: { played: number; playedSeconds: number }) => {
      if (!seeking) {
        const prog = state.played * 100;
        setProgress(prog);
        setTimer(state.playedSeconds);
        setProgressTracker(state.playedSeconds);
        onProgress(prog);
      }
    }, [seeking, onProgress, setProgressTracker]);

    const handleDuration = useCallback((dur: any) => {
      setDuration(dur);
      setCountDown(`${formatTime(dur)}`);
      onDuration(dur);
    }, [onDuration]);

    const handleSeekChange = useCallback((value: number) => {
      setSeeking(true);
      if (playerRef.current) {
        const newProgress = Math.min(Math.max(progress + value, 0), 100);
        playerRef.current.seekTo(newProgress / 100);
        setProgress(newProgress);
        onProgress(newProgress / 100);
      }
      setSeeking(false);
    }, [progress, onProgress]);

    const handlePlayPause = useCallback((status: boolean) => {
      setPlaying((prev) => !prev);
      onPause(status? "PLAYING": "PLAYED");
    }, [onPause]);

    const handleVideoEnded = useCallback(() => {
      
      setPlaying(false);
      onVideoEnded();
     /*  if(playerRef.current){
        playerRef.current.seekTo(0);
      } */
      
    }, [onVideoEnded]);

    const handleVideoReady = useCallback(() => {
      if (isVideoLoaded) return;
      setIsVideoLoaded(true);
      
      
      if (playerRef.current) {
        const internalPlayer = playerRef.current.getInternalPlayer();
        if (internalPlayer instanceof HTMLVideoElement) {
          videoElementRef.current = internalPlayer;
          
          
          if (videoElementRef.current && window.IntersectionObserver) {
            const observer = new IntersectionObserver(
              ([entry]) => {
                setIsVideoVisible(entry.isIntersecting);
              },
              { threshold: 0.5 }
            );
            observer.observe(videoElementRef.current);
          }
        }
      }
    }, [isVideoLoaded]);

    useEffect(() => {
      if (!videoElementRef.current || !window.IntersectionObserver) return;
      
      const rect = videoElementRef.current.getBoundingClientRect();
 
      
      
      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsVideoVisible(entry.isIntersecting);
        },
        { 
          threshold: 0.1, 
          rootMargin: "0px"
        }
      );
      
      observer.observe(videoElementRef.current);
      
      return () => observer.disconnect();
    }, [videoElementRef.current]);

    const [, forceUpdate] = useState({});
    useEffect(() => {
      // Force re-render when video source changes
      forceUpdate({});
    }, [vidSrc]);

    useEffect(() => {
      
      if (!initialVidSrcRef.current) {
        initialVidSrcRef.current = true;
        previousSourceRef.current = vidSrc;
        
        
        let url = "";
        if (typeof vidSrc === 'string') {
          url = vidSrc;
        } else if (vidSrc instanceof Blob) {
          url = URL.createObjectURL(vidSrc);
        }
        sourceUrlRef.current = url;
        return;
      }
      
      
      let isEqual = false;
      
      if (vidSrc === previousSourceRef.current) {
        isEqual = true;
      } else if (vidSrc instanceof Blob && previousSourceRef.current instanceof Blob) {
        isEqual = false;
      } else if (typeof vidSrc === 'object' && vidSrc !== null && 
                typeof previousSourceRef.current === 'object' && previousSourceRef.current !== null) {
        try {
          isEqual = JSON.stringify(vidSrc) === JSON.stringify(previousSourceRef.current);
        } catch (e) {
          isEqual = false;
        }
      }else if (typeof vidSrc === 'string' && previousSourceRef.current instanceof String && vidSrc !== previousSourceRef.current){
        isEqual = false;
      }
      
      if (!isEqual) {
        previousSourceRef.current = vidSrc;
        
        
        if (sourceUrlRef.current && sourceUrlRef.current.startsWith('blob:')) {
          URL.revokeObjectURL(sourceUrlRef.current);
        }
        
        
        let url = "";
        if (typeof vidSrc === 'string') {
          url = vidSrc;
        } else if (vidSrc instanceof Blob) {
          url = URL.createObjectURL(vidSrc);
        }
        
        
        sourceUrlRef.current = url;
      }
      setIsVideoLoaded(false);
      
      return () => {
        if (sourceUrlRef.current && sourceUrlRef.current.startsWith('blob:')) {
          URL.revokeObjectURL(sourceUrlRef.current);
        }
      };
    }, [vidSrc]);

    useEffect(() => {
      if (previousPlayRef.current !== play) {
        previousPlayRef.current = play;
        setPlaying(!!play);
      }
    }, [play]);

    
    useEffect(() => {
      onReady(isVideoLoaded && isVideoVisible);
    }, [isVideoLoaded, isVideoVisible]);

    
    useEffect(() => {
      setPlaying(!!autoPlay);
    }, [autoPlay]);

    
    useEffect(() => {
      if (progress > 0) {
        setCountDown(`${formatTime(duration)} left`);
      }
    }, [progress, duration, timer]);
    
    
    const memoizedPlayer = useMemo(() => {
      const currentUrl = sourceUrlRef.current;
      return (
        <ReactPlayer
          ref={setPlayerRef}
          url={currentUrl}
          width={"100%"}
          height={"100%"}
          controls={false}
          preload="auto"
          pip={false}
          config={{ file: { attributes: { playsInline: true, autoplay:false, muted:false, preload: "auto" }, }, }}
          progressInterval={100}
          playsinline={true}
          playing={playing}
          onEnded={handleVideoEnded}
          onProgress={handleProgress}
          onDuration={handleDuration}
          onReady={(e)=>{handleVideoReady()}}
          onError={(error) => {
            // console.error("ReactPlayer error:", error);handleDuration(e.getDuration());
            if (onReady) onReady(false);
          }}
          style={{
            borderRadius: "50%",
            overflow: "hidden",
            margin: "auto",
          }}
          {...rest}
        />
      );
    }, [
      vidSrc,
      sourceUrlRef.current,
      playing,
      handleVideoEnded,
      handleProgress,
      handleDuration,
      handleVideoReady,
      setPlayerRef,
      rest
    ]);

    
    const memoizedControls = useMemo(() => (
      !hidePlayVideoIcon && !playing && (
        <PlayerControls 
          handlePlayPause={handlePlayPause} 
          playing={playing} 
          handleSeekChange={handleSeekChange} 
          hideSeek={hideSeek} 
          size={playsize} 
        />
      )
    ), [hidePlayVideoIcon, playing, handlePlayPause, handleSeekChange, hideSeek, playsize]);

    return (
      <CircularProgress
        size={size}
        strokeWidth={showProgress ? 7 : 0}
        progress={progress}
        progressColor="#8451D6"
        trailColor="#ddd"
        startAngle={-90}
        onFinish={() => alert("Progress Completed!")}
      >
        <StyledPlayerWrapper>
          {memoizedPlayer}
        </StyledPlayerWrapper>
        {showTimer && (<TimerCounter>{countDown}</TimerCounter>)} 
        {memoizedControls}
      </CircularProgress>
    );
  }
);


export default memo(VideoPlayer, (prevProps, nextProps) => {
  const vidSrcEqual = 
    prevProps.vidSrc === nextProps.vidSrc || 
    (typeof prevProps.vidSrc === 'object' && 
     typeof nextProps.vidSrc === 'object' && 
     JSON.stringify(prevProps.vidSrc) === JSON.stringify(nextProps.vidSrc)) ||
     (typeof prevProps.vidSrc === 'string' && 
      typeof nextProps.vidSrc === 'string' && 
      prevProps.vidSrc === nextProps.vidSrc);

  return (
    vidSrcEqual &&
    prevProps.play === nextProps.play &&
    prevProps.autoPlay === nextProps.autoPlay &&
    prevProps.size === nextProps.size
  );
});